import { Tooltip } from 'antd'
import { NonCompliantAffiliate } from '../../../../../app/api'
import CountryList from '../../../../../shared/ui/cells/CountriesCell'
import { cellClass, Table, TableCell } from '../../../../../shared/ui/table/Table'
import { useState } from 'react'
import { BpButton } from '../../../../../shared/components/buttons/bp-button/BpButton'
import { IconLink } from '../../../../../shared/components/icons/IconLink'
import { Link } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../../../../app/store/hooks/Redux'
import { AdvertiserReportFilterInterface } from '../../../model/types/AdvertiserReportFilter.interface'
import { selectAdvertiserReportFilters } from '../../../model/paidResultsSlice'
import { useTranslation } from 'react-i18next'
import { adOverviewLink, ppcComplianceLink } from './ReportLinks'

interface IAffiliatesTabProps {
  advertiser: string
  affiliates: NonCompliantAffiliate[]
  total: number
}

export const AffiliatesTab = ({ advertiser, affiliates, total }: IAffiliatesTabProps) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const [countRows, setCountRows] = useState<number>(5)
  const filters = useAppSelector<AdvertiserReportFilterInterface>(selectAdvertiserReportFilters)

  const HEADERS = [
    { label: t('table.columns.affiliateId') },
    { label: t('table.columns.ads'), width: '100px' },
    {
      label: t('table.columns.network'),
      tooltip: t('pages.paidResults.networkTooltip'),
      width: '172px',
    },
    { label: t('table.columns.countries'), width: '172px' },
    { label: t('table.columns.lastFound'), width: '172px' },
    { label: '' },
  ]

  return (
    <div className={'flex flex-col gap-[16px] border-t border-[#E5E1EA] pb-[16px]'}>
      <Table headers={HEADERS} isEmpty={!affiliates.length}>
        {affiliates.slice(0, countRows + 1).map((item, index) => (
          <tr key={index} className={'relative'}>
            <TableCell className={cellClass}>
              <div className={'flex flex-col gap-[4px]'}>
                <div>
                  <span className={'truncate whitespace-nowrap text-[15px]'}>
                    {item.affiliate_name ??
                      (item.pub_value?.length ? item.pub_value : item.affiliate)}
                  </span>
                </div>
                {(item.affiliate_name || item.pub_value) && (
                  <Tooltip
                    className='w-fit max-w-[380px] truncate text-[10px] leading-none'
                    title={item.affiliate}
                  >
                    {item.affiliate}
                  </Tooltip>
                )}
              </div>
            </TableCell>
            <TableCell>{item.ads_count}</TableCell>
            <TableCell>
              <div className={'flex flex-col justify-center gap-[4px]'}>
                <p>{item.network_name ? item.network_name : 'Not specified'}</p>
                {item.network_value && (
                  <span className='text-[10px] leading-none'>{item.network_value}</span>
                )}
              </div>
            </TableCell>
            <TableCell>
              <CountryList countries={item.countries} max={3} />
            </TableCell>
            <TableCell>{item.last_found}</TableCell>
            <TableCell>
              <Link
                {...adOverviewLink(
                  filters,
                  { adDomain: advertiser, affiliate: String(item.id) },
                  dispatch,
                )}
              >
                <button className={'text-focus'}>{t('pages.paidResults.showAllAds')}</button>
              </Link>
            </TableCell>
            {index === countRows && (
              <div
                className={
                  'absolute top-0 left-0 flex h-full w-full items-center justify-center bg-white/[.9]'
                }
              >
                <button
                  className={'text-[14px] font-semibold text-default'}
                  onClick={() => setCountRows(affiliates.length)}
                >
                  {t('pages.paidResults.showMore', {
                    count: total - countRows,
                  })}
                </button>
              </div>
            )}
          </tr>
        ))}
      </Table>
      {!!affiliates?.length && (
        <div className={'flex w-full justify-center'}>
          <Link {...ppcComplianceLink(filters, { site: advertiser }, dispatch)}>
            <BpButton className={'uppercase'} iconLeft={<IconLink />}>
              {t('pages.paidResults.goToAffiliates')}
            </BpButton>
          </Link>
        </div>
      )}
    </div>
  )
}
